/**
 * Perf-query capture mount / canvas lifecycle diagnostics.
 * Updated only when callers gate on ?perf=1 (or tests force-enable).
 */
export interface PerfCaptureLifecycleSnapshot {
  queueActive: number;
  queueMaxActive: number;
  captureMountsActive: number;
  captureMountsPeak: number;
  captureCanvasPeak: number;
  liveCanvasAtSampleStart: number;
  contextLossEvents: number;
}

let enabled = false;
let captureMountsActive = 0;
let captureMountsPeak = 0;
let captureCanvasPeak = 0;
let liveCanvasAtSampleStart = 0;
let contextLossEvents = 0;
let queueActive = 0;
let queueMaxActive = 0;

export function beginPerfCaptureLifecycle(search?: string): boolean {
  const params = new URLSearchParams(
    (search ?? (typeof window !== "undefined" ? window.location.search : "")).replace(/^\?/, ""),
  );
  enabled = params.get("perf") === "1";
  captureMountsActive = 0;
  captureMountsPeak = 0;
  captureCanvasPeak = 0;
  liveCanvasAtSampleStart =
    typeof document !== "undefined" ? document.querySelectorAll("canvas").length : 0;
  contextLossEvents = 0;
  queueActive = 0;
  queueMaxActive = 0;
  mirrorToDataset();
  return enabled;
}

export function forceEnablePerfCaptureLifecycleForTests(): void {
  enabled = true;
  captureMountsActive = 0;
  captureMountsPeak = 0;
  captureCanvasPeak = 0;
  liveCanvasAtSampleStart = 0;
  contextLossEvents = 0;
  queueActive = 0;
  queueMaxActive = 0;
}

export function resetPerfCaptureLifecycleForTests(): void {
  enabled = false;
  captureMountsActive = 0;
  captureMountsPeak = 0;
  captureCanvasPeak = 0;
  liveCanvasAtSampleStart = 0;
  contextLossEvents = 0;
  queueActive = 0;
  queueMaxActive = 0;
}

function mirrorToDataset(): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.perfCaptureLifecycle = JSON.stringify({
    queueActive,
    queueMaxActive,
    captureMountsActive,
    captureMountsPeak,
    captureCanvasPeak,
    liveCanvasAtSampleStart,
    contextLossEvents,
  });
}

export function notePerfCaptureQueue(active: number, maxActive: number): void {
  if (!enabled) return;
  queueActive = active;
  queueMaxActive = Math.max(queueMaxActive, maxActive, active);
  mirrorToDataset();
}

export function notePerfCaptureMountEnter(canvasCount = 1): void {
  if (!enabled) return;
  captureMountsActive += 1;
  captureMountsPeak = Math.max(captureMountsPeak, captureMountsActive);
  captureCanvasPeak = Math.max(captureCanvasPeak, canvasCount);
  mirrorToDataset();
}

export function notePerfCaptureMountExit(): void {
  if (!enabled) return;
  captureMountsActive = Math.max(0, captureMountsActive - 1);
  mirrorToDataset();
}

export function notePerfCaptureCanvasCount(n: number): void {
  if (!enabled) return;
  captureCanvasPeak = Math.max(captureCanvasPeak, n);
  mirrorToDataset();
}

export function notePerfContextLoss(): void {
  if (!enabled) return;
  contextLossEvents += 1;
  mirrorToDataset();
}

export function getPerfCaptureLifecycleSnapshot(): PerfCaptureLifecycleSnapshot {
  return {
    queueActive,
    queueMaxActive,
    captureMountsActive,
    captureMountsPeak,
    captureCanvasPeak,
    liveCanvasAtSampleStart,
    contextLossEvents,
  };
}
